import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches } from "@/lib/employeeSearch";
import { toast } from "sonner";
import { FileText, Send, RefreshCw, AlertTriangle, CheckCircle2, Clock, Trash2, Download, Search } from "lucide-react";

function formatBytes(n) {
    if (!n || n <= 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function statusStyle(status) {
    switch (status) {
        case "expired":
            return { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", label: "Expired" };
        case "expiring_soon":
            return { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", label: "Expiring soon" };
        default:
            return { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", label: "Active" };
    }
}

function daysUntil(iso) {
    if (!iso || iso.length < 10) return null;
    const d = new Date(iso.slice(0, 10) + "T00:00:00");
    if (isNaN(d)) return null;
    const ms = d.getTime() - new Date().setHours(0, 0, 0, 0);
    return Math.round(ms / 86400000);
}

export default function Documents() {
    const [docs, setDocs] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | expiring | expired
    const [search, setSearch] = useState("");

    const load = async () => {
        try {
            const [d, e] = await Promise.all([
                api.get("/documents"),
                api.get("/employees"),
            ]);
            setDocs(d.data || []);
            setEmployees(e.data || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Failed to load documents");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const empById = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

    const filtered = useMemo(() => {
        let list = docs;
        if (filter === "expiring") list = list.filter((d) => d.status === "expiring_soon");
        if (filter === "expired") list = list.filter((d) => d.status === "expired");
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter((d) => {
                const emp = empById[d.employee_id];
                return (
                    d.doc_type_label.toLowerCase().includes(q) ||
                    d.file_name.toLowerCase().includes(q) ||
                    (emp && employeeMatches(emp, q))
                );
            });
        }
        return list;
    }, [docs, filter, search, empById]);

    const stats = useMemo(() => {
        const total = docs.length;
        const expired = docs.filter((d) => d.status === "expired").length;
        const expiring = docs.filter((d) => d.status === "expiring_soon").length;
        return { total, expired, expiring, active: total - expired - expiring };
    }, [docs]);

    const onDownload = async (doc) => {
        try {
            const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = doc.file_name || "document.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Download failed");
        }
    };

    const onDelete = async (doc) => {
        if (!window.confirm(`Delete "${doc.file_name}"? This can't be undone.`)) return;
        try {
            await api.delete(`/documents/${doc.id}`);
            toast.success("Document deleted");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="documents-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Compliance & ID</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.02]">
                        Documents
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-2xl">
                        Employee credentials, ID cards and certificates. Reminders auto-fire 30, 14 and 7 days before expiry to both HR and the employee.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2.5 text-sm hover:bg-ink hover:text-white transition-colors"
                        data-testid="docs-refresh-btn"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10" data-testid="docs-stats">
                <Stat label="Total documents" value={loading ? "—" : stats.total} icon={FileText} />
                <Stat label="Active" value={loading ? "—" : stats.active} icon={CheckCircle2} accent="emerald" />
                <Stat label="Expiring ≤30d" value={loading ? "—" : stats.expiring} icon={Clock} accent="amber" />
                <Stat label="Expired" value={loading ? "—" : stats.expired} icon={AlertTriangle} accent="red" />
            </div>

            {/* Filters */}
            <div className="mt-10 flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search name, staff no, department, division, document…"
                        className="w-full pl-10 pr-3 py-2.5 border border-ink/15 text-sm focus:border-klein focus:outline-none bg-white"
                        data-testid="docs-search"
                    />
                </div>
                <div className="flex gap-px bg-ink/10 border border-ink/10">
                    {[
                        { id: "all", label: "All" },
                        { id: "expiring", label: `Expiring (${stats.expiring})` },
                        { id: "expired", label: `Expired (${stats.expired})` },
                    ].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            className={`px-4 py-2.5 text-sm transition-colors ${filter === f.id ? "bg-ink text-white" : "bg-white text-ink/70 hover:bg-bone"}`}
                            data-testid={`docs-filter-${f.id}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="mt-6 bg-white border border-ink/10" data-testid="docs-table">
                {loading ? (
                    <div className="p-10 text-sm text-ink/40">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-sm text-ink/50" data-testid="docs-empty">
                        {docs.length === 0
                            ? "No documents on file yet. Open an employee's profile and click 'Send upload link' to invite them to upload PDFs."
                            : "No documents match your search/filter."}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-ink/50 border-b border-ink/10">
                                    <th className="px-6 py-3 font-medium">Employee</th>
                                    <th className="px-4 py-3 font-medium">Document</th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                    <th className="px-4 py-3 font-medium">Expires</th>
                                    <th className="px-4 py-3 font-medium">Uploaded</th>
                                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((d) => {
                                    const emp = empById[d.employee_id];
                                    const st = statusStyle(d.status);
                                    const days = daysUntil(d.expiry_date);
                                    return (
                                        <tr key={d.id} className="border-b border-ink/5 hover:bg-bone" data-testid={`doc-row-${d.id}`}>
                                            <td className="px-6 py-3.5">
                                                {emp ? (
                                                    <Link to={`/employees/${emp.id}`} className="hover:text-klein">
                                                        <div className="text-ink">{emp.first_name} {emp.last_name}</div>
                                                        <div className="text-xs text-ink/50">{emp.employee_no || "—"} · {emp.position || ""}</div>
                                                    </Link>
                                                ) : (
                                                    <span className="text-ink/40 italic">Unknown</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <div className="text-ink">{d.doc_type_label}</div>
                                                <div className="text-xs text-ink/50 truncate max-w-[200px]" title={d.file_name}>{d.file_name} · {formatBytes(d.file_size)}</div>
                                            </td>
                                            <td className="px-4 py-3.5">
                                                <span className={`inline-flex items-center px-2 py-1 border ${st.bg} ${st.border} ${st.text} text-[10px] uppercase tracking-[0.15em]`}>
                                                    {st.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3.5 tabular-nums">
                                                {d.expiry_date ? (
                                                    <>
                                                        <div className="text-ink">{d.expiry_date.slice(0, 10)}</div>
                                                        {days !== null && (
                                                            <div className={`text-xs ${days < 0 ? "text-red-600" : days <= 30 ? "text-amber-600" : "text-ink/50"}`}>
                                                                {days < 0 ? `${-days}d ago` : `in ${days}d`}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    <span className="text-ink/30">—</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3.5 text-xs text-ink/60">
                                                <div>{d.uploaded_at?.slice(0, 10)}</div>
                                                <div className="text-ink/40 truncate max-w-[140px]" title={d.uploaded_by_name}>by {d.uploaded_by_name}</div>
                                            </td>
                                            <td className="px-4 py-3.5 text-right">
                                                <div className="inline-flex gap-1">
                                                    <button
                                                        onClick={() => onDownload(d)}
                                                        className="p-2 hover:bg-ink/5 text-ink/60 hover:text-klein"
                                                        title="Download"
                                                        data-testid={`doc-download-${d.id}`}
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => onDelete(d)}
                                                        className="p-2 hover:bg-red-50 text-ink/60 hover:text-red-600"
                                                        title="Delete"
                                                        data-testid={`doc-delete-${d.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <p className="mt-6 text-xs text-ink/50 flex items-center gap-2">
                <Send className="w-3 h-3" /> To request documents from an employee, open their profile and click <strong className="text-ink">"Send upload link"</strong> — they'll get a 3-day expiring link by email.
            </p>
        </div>
    );
}

function Stat({ label, value, icon: Icon, accent }) {
    const colorMap = {
        emerald: "text-emerald-600",
        amber: "text-amber-600",
        red: "text-red-600",
    };
    return (
        <div className="bg-white p-6">
            <div className="flex items-start justify-between">
                <div className="overline">{label}</div>
                <Icon className={`w-4 h-4 ${colorMap[accent] || "text-ink/30"}`} strokeWidth={1.5} />
            </div>
            <div className={`mt-3 font-display font-black tracking-tighter text-3xl tabular-nums ${colorMap[accent] || "text-ink"}`}>
                {value}
            </div>
        </div>
    );
}
